/**
 * Release manifest + telemetry client (Card 10).
 *
 * Deliberately uses plain `fetch` rather than `http-client.ts`: both endpoints are public, and the
 * shared client dispatches a logout on any 401 — an update prompt must never be able to sign a
 * user out. It also retries, which is wrong for a fire-and-forget analytics beacon.
 *
 * Contract: src/docs/MOBILE_VERSION_SCHEME.md §4
 */

import type {
  ReleaseChannel,
  ReleaseEventInput,
  ReleaseManifest,
  ReleasePlatform
} from '@/types/release';

/** Mirrors http-client.ts: VITE_API_URL is the origin, `/api` is appended. */
function apiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  return envUrl ? `${envUrl}/api` : '/api';
}

const MANIFEST_TIMEOUT_MS = 8000;

/**
 * Fetch the current release manifest for a channel/platform.
 *
 * Returns `null` on ANY failure — offline, 404 (nothing published yet), 503, malformed JSON.
 * A failed manifest fetch is not an error state for the user: no banner, no toast. Callers must
 * treat `null` as "no update information", never as "no update available" plus an error.
 */
export async function fetchReleaseManifest(
  platform: ReleasePlatform,
  channel: ReleaseChannel
): Promise<ReleaseManifest | null> {
  const url = `${apiBaseUrl()}/releases/manifest?channel=${encodeURIComponent(channel)}&platform=${encodeURIComponent(platform)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(MANIFEST_TIMEOUT_MS)
    });
    if (!response.ok) return null;

    const manifest = (await response.json()) as ReleaseManifest;
    // Guard against a proxy handing back an HTML error page with a 200.
    if (!manifest || typeof manifest.version !== 'string') return null;
    return manifest;
  } catch {
    return null;
  }
}

/**
 * Record a download/adoption event. Fire-and-forget: resolves `false` on any failure and never
 * throws, so a telemetry outage cannot break the update prompt. The endpoint itself stays
 * truthful (it answers 500 when the write fails); swallowing happens here, on the client.
 */
export async function postReleaseEvent(input: ReleaseEventInput): Promise<boolean> {
  try {
    const response = await fetch(`${apiBaseUrl()}/releases/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      // Lets the POST outlive the page/webview teardown that a download CTA triggers.
      keepalive: true
    });
    return response.ok;
  } catch {
    return false;
  }
}
